/**
 * Fetches /api/config/subscription once and caches it.
 * Falls back to built-in defaults (shared/subscription-defaults.ts — same file
 * the server uses) if the fetch fails, so the app never breaks when the
 * endpoint is unreachable. A failed fetch is retried on the next call instead
 * of being cached for the whole session.
 *
 * Components that need to react when the config arrives should use the
 * useSubscriptionConfig() hook (src/hooks/useSubscriptionConfig.js), which
 * subscribes via subscribeSubscriptionConfig().
 */
import apiClient from './apiConfig';
import { DEFAULT_FREE_LIMITS, DEFAULT_PRO_LIMITS } from '../../shared/subscription-defaults';

const BACKEND_URL = import.meta.env.VITE_BACKEND_URL || '';

export const DEFAULT_SUBSCRIPTION_CONFIG = {
  billingMode: 'paid',
  freeForAllActive: false,
  freeModeEndsAt: null,
  plans: [],
  freeLimits: DEFAULT_FREE_LIMITS,
  proLimits: DEFAULT_PRO_LIMITS,
  configVersion: 'default',
};

let cachedConfig = DEFAULT_SUBSCRIPTION_CONFIG;
let fetchPromise = null;

const listeners = new Set();
const notify = () => listeners.forEach((cb) => cb());

/** Subscribe to config updates (for useSyncExternalStore). Returns unsubscribe. */
export const subscribeSubscriptionConfig = (cb) => {
  listeners.add(cb);
  return () => listeners.delete(cb);
};

/** Kick off (or reuse) the config fetch. Safe to call multiple times. */
export const loadSubscriptionConfig = () => {
  if (!fetchPromise) {
    fetchPromise = apiClient
      .get(`${BACKEND_URL}/api/config/subscription`)
      .then((res) => {
        cachedConfig = { ...DEFAULT_SUBSCRIPTION_CONFIG, ...res.data };
        notify();
        return cachedConfig;
      })
      .catch(() => {
        // Keep defaults, but allow the next caller to retry instead of
        // pinning a boot-time network blip on the whole session.
        fetchPromise = null;
        return cachedConfig;
      });
  }
  return fetchPromise;
};

/** Synchronous access to the latest known config (defaults until fetch resolves). */
export const getCachedSubscriptionConfig = () => cachedConfig;

/**
 * Force a refetch (used on tab focus + interval). Notifies listeners only
 * when the payload actually changed — comparison is by full JSON, not just
 * configVersion, because freeForAllActive can flip on promo expiry without
 * the DB row (and therefore the version) changing.
 */
export const refreshSubscriptionConfig = async () => {
  try {
    const res = await apiClient.get(`${BACKEND_URL}/api/config/subscription`);
    const next = { ...DEFAULT_SUBSCRIPTION_CONFIG, ...res.data };
    if (JSON.stringify(next) !== JSON.stringify(cachedConfig)) {
      cachedConfig = next;
      notify();
    }
  } catch {
    // Transient failure: keep serving the last known config.
  }
  return cachedConfig;
};
